import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import {
  Breadcrumb,
  Button,
  Empty,
  Input,
  Modal,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  getBot,
  getBotConversations,
  getBotConversationMessages,
  deleteBotMessage,
  getBotRefusedMessages,
  draftRefusedAnswer,
  addCollectionText,
} from '../../services/api/bots';
import { getAssistants } from '../../services/api';
import { getPipelineTraceVisibility } from '../../services/api/platformSettings';
import { fetchAssistant } from '../../services/api';
import { assistantDisplayImageSrc } from '../../utils/assistantVisual';
import { relativeTime } from '../../utils/relativeTime';
import MessengerLayout, { loadContextPanelOpen, saveContextPanelOpen } from '../../components/messenger/MessengerLayout';
import ConversationFilterBar from '../../components/messenger/ConversationFilterBar';
import ChatListItem from '../../components/messenger/ChatListItem';
import ChatMessageBubble from '../../components/messenger/ChatMessageBubble';
import BotActivityContextPanel from '../../components/messenger/BotActivityContextPanel';
import InteractionClusterDivider from '../../components/messenger/InteractionClusterDivider';
import { clusterMessages } from '../../components/messenger/clusterMessages';
import '../../components/messenger/BotActivity.css';

const { Text, Title } = Typography;
const { TextArea } = Input;

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'groups', label: 'Groups' },
  { value: 'private', label: 'Private' },
  { value: 'declined', label: 'Declined' },
];

function refuseLabel(reason) {
  if (reason === 'off_topic') return 'Off-topic';
  if (reason === 'no_data') return 'No data';
  return reason || 'Refused';
}

function matchesFilter(conversation, filter) {
  if (filter === 'all') return true;
  if (filter === 'groups') {
    return conversation.chat_type && conversation.chat_type !== 'private';
  }
  if (filter === 'private') return conversation.chat_type === 'private';
  if (filter === 'declined') return (conversation.declined_count || 0) > 0;
  return true;
}

export default function BotSessionsMessenger() {
  const { botId, chatId: chatIdParam } = useParams();
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();
  const bid = useMemo(() => parseInt(String(botId), 10), [botId]);
  const selectedChatId = chatIdParam ? decodeURIComponent(chatIdParam) : null;

  const [bot, setBot] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [refused, setRefused] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [contextOpen, setContextOpen] = useState(false);
  const [pipelineVis, setPipelineVis] = useState(null);
  const [linkedAssistant, setLinkedAssistant] = useState(null);
  const [knowledgeModal, setKnowledgeModal] = useState(null);
  const [draftLoading, setDraftLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [collectionId, setCollectionId] = useState(null);

  const messageRefs = useRef(new Map());

  const assistantImg = useMemo(
    () => assistantDisplayImageSrc(linkedAssistant),
    [linkedAssistant],
  );

  const showPipelineTrace = Boolean(
    pipelineVis?.pipeline_trace_enabled && pipelineVis?.pipeline_trace_show_widget_admin,
  );

  const loadBot = useCallback(async () => {
    if (!keycloak?.token || !Number.isFinite(bid)) return;
    setLoading(true);
    try {
      const [b, convs, r] = await Promise.all([
        getBot(keycloak, bid),
        getBotConversations(keycloak, bid),
        getBotRefusedMessages(keycloak, bid),
      ]);
      setBot(b);
      setConversations(Array.isArray(convs) ? convs : []);
      setRefused(Array.isArray(r) ? r : []);
      const assistants = await getAssistants(keycloak, false);
      const asst = assistants.find((a) => a.id === b.assistant_id);
      setCollectionId(asst?.data_collection_id || null);
    } catch (e) {
      message.error(e.message || 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [keycloak, bid]);

  useEffect(() => { loadBot(); }, [loadBot]);

  useEffect(() => {
    if (!keycloak?.authenticated || !bot?.assistant_id) {
      setLinkedAssistant(null);
      return;
    }
    let cancelled = false;
    fetchAssistant(keycloak, bot.assistant_id)
      .then((a) => { if (!cancelled) setLinkedAssistant(a); })
      .catch(() => { if (!cancelled) setLinkedAssistant(null); });
    return () => { cancelled = true; };
  }, [keycloak, bot?.assistant_id]);

  useEffect(() => {
    if (!keycloak) return;
    getPipelineTraceVisibility(keycloak)
      .then(setPipelineVis)
      .catch(() => setPipelineVis(null));
  }, [keycloak]);

  useEffect(() => {
    if (!keycloak?.token || !Number.isFinite(bid) || !selectedChatId) {
      setMessages([]);
      return undefined;
    }
    let cancelled = false;
    setMessagesLoading(true);
    getBotConversationMessages(keycloak, bid, selectedChatId)
      .then((m) => { if (!cancelled) setMessages(m || []); })
      .catch((e) => message.error(e.message || 'Failed to load messages'))
      .finally(() => { if (!cancelled) setMessagesLoading(false); });
    return () => { cancelled = true; };
  }, [keycloak, bid, selectedChatId]);

  useEffect(() => {
    if (!selectedChatId) {
      setContextOpen(false);
      return;
    }
    setContextOpen(loadContextPanelOpen());
  }, [selectedChatId]);

  const selectedConversation = useMemo(
    () => conversations.find((c) => c.platform_chat_id === selectedChatId) || null,
    [conversations, selectedChatId],
  );

  const filteredConversations = useMemo(
    () => conversations.filter((c) => matchesFilter(c, filter)),
    [conversations, filter],
  );

  const filterCounts = useMemo(() => ({
    declined: refused.length,
  }), [refused.length]);

  const clusteredItems = useMemo(() => clusterMessages(messages), [messages]);

  const goChat = useCallback((platformChatId, messageId) => {
    if (!Number.isFinite(bid)) return;
    const path = `/channels/bots/${bid}/activity/${encodeURIComponent(platformChatId)}`;
    navigate(path);
    if (messageId) {
      setTimeout(() => {
        const el = messageRefs.current.get(Number(messageId));
        el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      }, 300);
    }
  }, [bid, navigate]);

  const toggleContext = () => {
    setContextOpen((open) => {
      const next = !open;
      saveContextPanelOpen(next);
      return next;
    });
  };

  const openKnowledgeModal = async (item) => {
    setKnowledgeModal({ item, question: item.content, answer: '' });
    setDraftLoading(true);
    try {
      const draft = await draftRefusedAnswer(keycloak, bid, item.id, item.content);
      setKnowledgeModal((prev) => prev && ({
        ...prev,
        question: draft.question,
        answer: draft.answer,
      }));
    } catch (e) {
      message.error(e.message || 'Failed to generate draft');
    } finally {
      setDraftLoading(false);
    }
  };

  const saveToCollection = async () => {
    if (!collectionId) {
      message.error('Assistant has no data collection linked');
      return;
    }
    if (!knowledgeModal?.question?.trim() || !knowledgeModal?.answer?.trim()) {
      message.error('Question and answer are required');
      return;
    }
    setSaveLoading(true);
    try {
      await addCollectionText(keycloak, collectionId, {
        title: knowledgeModal.question.slice(0, 120),
        content: `**Q:** ${knowledgeModal.question}\n\n**A:** ${knowledgeModal.answer}`,
      });
      message.success('Added to knowledge collection');
      setKnowledgeModal(null);
    } catch (e) {
      message.error(e.message || 'Failed to save');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleQuoteClick = (replyToMessageId) => {
    const el = messageRefs.current.get(Number(replyToMessageId));
    if (el) {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      el.classList.add('messenger-message-highlight');
      setTimeout(() => el.classList.remove('messenger-message-highlight'), 1500);
    }
  };

  const handleDeleteMessage = (msg) => {
    Modal.confirm({
      title: 'Delete message?',
      content: 'This removes the message from the activity view and from future bot context. It stays in the database as deleted.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await deleteBotMessage(keycloak, bid, msg.id);
          setMessages((prev) => prev.filter((m) => m.id !== msg.id));
          message.success('Message deleted');
        } catch (e) {
          message.error(e.message || 'Failed to delete message');
          throw e;
        }
      },
    });
  };

  const sidebar = (
    <>
      <div className="bam-sidebar-header">
        <Breadcrumb
          className="wsm-breadcrumb"
          items={[
            { title: <Link to="/channels/bots">Bots</Link> },
            { title: bot?.name || 'Bot' },
            { title: 'Messages' },
          ]}
        />
        <div className="bam-sidebar-heading">
          <Title level={5} className="bam-sidebar-title">
            {bot?.name || 'Bot'}
          </Title>
          <Text type="secondary" className="bam-sidebar-subtitle">
            Chats where the bot replied
          </Text>
        </div>
        <div className="bam-sidebar-tabs">
          <ConversationFilterBar
            filters={FILTERS}
            value={filter}
            onChange={setFilter}
            counts={filterCounts}
          />
        </div>
      </div>
      <div className="bam-sidebar-list">
        {loading ? (
          <div style={{ padding: 24, textAlign: 'center' }}><Spin /></div>
        ) : filter === 'declined' ? (
          refused.length === 0 ? (
            <Empty style={{ marginTop: 24 }} description="No declined questions" />
          ) : (
            <div className="bam-declined-list">
              {refused.map((item) => (
                <div key={item.id} className="bam-declined-card">
                  <div className="bam-declined-card-header">
                    <Tag color={item.refuse_reason === 'no_data' ? 'gold' : 'default'}>
                      {refuseLabel(item.refuse_reason)}
                    </Tag>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {relativeTime(item.created_at)}
                    </Text>
                  </div>
                  <Text strong style={{ fontSize: 12 }}>
                    {item.sender_name || 'User'}
                  </Text>
                  <div style={{ marginTop: 6, fontSize: 13 }}>
                    {item.meta?.trigger_text || item.content}
                  </div>
                  <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                    <Button size="small" onClick={() => openKnowledgeModal(item)}>
                      Add to knowledge
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : filteredConversations.length === 0 ? (
          <Empty style={{ marginTop: 24 }} description="No conversations yet" />
        ) : (
          filteredConversations.map((c) => (
            <ChatListItem
              key={c.platform_chat_id}
              conversation={c}
              active={c.platform_chat_id === selectedChatId}
              onClick={() => goChat(c.platform_chat_id)}
              platform={bot?.platform}
            />
          ))
        )}
      </div>
    </>
  );

  const mainHeader = selectedChatId ? (
    <div>
      <Title level={5} style={{ margin: 0, fontSize: 16 }}>
        {selectedConversation?.chat_type === 'private'
          ? (selectedConversation?.chat_title || selectedChatId)
          : `Group: ${selectedConversation?.chat_title || selectedChatId}`}
      </Title>
      {selectedConversation?.participants?.length > 0 && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          Participants: {selectedConversation.participants.join(', ')}
        </Text>
      )}
    </div>
  ) : (
    <Title level={5} style={{ margin: 0, fontSize: 16 }}>Conversations</Title>
  );

  const main = !selectedChatId ? (
    <div className="wsm-empty-center">
      <Empty description="Choose a conversation from the list" />
    </div>
  ) : messagesLoading ? (
    <div style={{ textAlign: 'center', padding: 48 }}><Spin size="large" /></div>
  ) : messages.length === 0 ? (
    <Empty description="No messages in this chat yet" />
  ) : (
    <div style={{ maxWidth: 820, margin: '0 auto' }}>
      {clusteredItems.map((item) => {
        if (item.type === 'divider') {
          return <InteractionClusterDivider key={item.key} label={item.label} />;
        }
        const msg = item.message;
        return (
          <div
            key={item.key}
            ref={(el) => {
              if (el) messageRefs.current.set(msg.id, el);
              else messageRefs.current.delete(msg.id);
            }}
          >
            <ChatMessageBubble
              msg={msg}
              botName={bot?.name || 'Bot'}
              assistantImg={assistantImg}
              showPipelineTrace={showPipelineTrace}
              onQuoteClick={handleQuoteClick}
              onDelete={handleDeleteMessage}
              platformChatId={selectedChatId}
              platform={bot?.platform}
            />
          </div>
        );
      })}
    </div>
  );

  if (!Number.isFinite(bid)) {
    return (
      <div className="bam-root" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <Text>Invalid bot</Text>
        <Button onClick={() => navigate('/channels/bots')}>Back</Button>
      </div>
    );
  }

  return (
    <>
      <MessengerLayout
        sidebar={sidebar}
        mainHeader={mainHeader}
        main={main}
        contextPanel={(
          <BotActivityContextPanel
            conversation={selectedConversation}
            bot={bot}
            onCopyChatId={() => message.success('Chat ID copied', 1.2)}
          />
        )}
        contextOpen={contextOpen}
        contextEnabled={Boolean(selectedChatId)}
        onToggleContext={toggleContext}
      />

      <Modal
        title="Add to knowledge collection"
        open={Boolean(knowledgeModal)}
        onCancel={() => setKnowledgeModal(null)}
        onOk={saveToCollection}
        okText="Save to collection"
        confirmLoading={saveLoading || draftLoading}
        width={640}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <Text strong>Question</Text>
            <TextArea
              rows={3}
              value={knowledgeModal?.question || ''}
              onChange={(e) => setKnowledgeModal((p) => p && { ...p, question: e.target.value })}
            />
          </div>
          <div>
            <Text strong>Answer</Text>
            <Spin spinning={draftLoading}>
              <TextArea
                rows={6}
                value={knowledgeModal?.answer || ''}
                onChange={(e) => setKnowledgeModal((p) => p && { ...p, answer: e.target.value })}
              />
            </Spin>
          </div>
          {!collectionId && (
            <Text type="warning">Link a data collection to the assistant to enable saving.</Text>
          )}
        </div>
      </Modal>
    </>
  );
}
